if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }
      }
      
      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal',
            },
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap,
              },
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5,
              },
              1536: {
                slidesPerView: 7,
              },
            };
            break;
        }
      }
      

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        // Get current variant information from the product-info element
        const productInfo = this.closest('product-main')?.querySelector('product-info') || 
                          document.querySelector('product-info');
        
        let currentVariant = null;
        let productTitle = '';
        
        if (productInfo) {
          currentVariant = productInfo.currentVariant;
          productTitle = productInfo.dataset.productTitle || '';
        }
        
        // Helper function to check if variant matches alt text with curly braces
        const doesVariantMatchAltText = (variant, mediaAlt) => {
          if (!variant || !variant.title || variant.title === '') {
            return true;
          }
          
          if (!mediaAlt || mediaAlt.trim() === '') {
            return true;
          }
          
          const braceMatch = mediaAlt.match(/\{([^}]+)\}/);
          
          if (!braceMatch) {
            return true;
          }
          
          const braceContent = braceMatch[1];
          const variantRequirements = braceContent.split(',').map(req => req.trim());
          
          const currentVariantTitle = (variant.title || '').toLowerCase().trim();
          const currentOptions = [
            (variant.option1 || '').toLowerCase().trim(),
            (variant.option2 || '').toLowerCase().trim(),
            (variant.option3 || '').toLowerCase().trim()
          ].filter(opt => opt);
          
          const allVariantText = [currentVariantTitle, ...currentOptions].join(' ').toLowerCase();
          
          for (const requirement of variantRequirements) {
            const requirementParts = requirement.split(/\s*[\/|]\s*/).map(part => part.trim().toLowerCase());
            
            const allPartsMatch = requirementParts.every(part => {
              if (currentVariantTitle === part) return true;
              if (currentOptions.some(opt => opt === part)) return true;
              if (allVariantText.includes(part)) return true;
              return false;
            });
            
            if (allPartsMatch && requirementParts.length > 0) {
              return true;
            }
          }
          
          return false;
        };
        
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        
        if (allMedia) {
          const isAltTextImageFeatureEnabled = this.dataset.altTextImageFeature === 'true' || productInfo?.dataset?.altTextImageFeature === 'true';
          
          // FILTER media items based on variant using curly brace logic ONLY if alt_text_image_feature is enabled
          const filteredMedia = allMedia.filter(media => {
            if (!isAltTextImageFeatureEnabled) {
              return true;
            }
            
            // If no variant is selected, show all media
            if (!currentVariant || !currentVariant.title || currentVariant.title === '') {
              return true;
            }
            
            const mediaAlt = media.getAttribute('data-media-alt') || '';
            const mediaType = media.getAttribute('data-media-type') || 'image';
            
            // Non-image media always shows
            if (mediaType !== 'image') {
              return true;
            }
            
            return doesVariantMatchAltText(currentVariant, mediaAlt);
          });
          
          // Create dataSource from FILTERED media
          filteredMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                  originalIndex: Array.from(allMedia).indexOf(media)
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                  originalIndex: Array.from(allMedia).indexOf(media)
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                  alt: media.dataset.mediaAlt,
                  originalIndex: Array.from(allMedia).indexOf(media)
                });
                break;
            }
          });
        }
        
        // Destroy existing lightbox if it exists
        if (this.lightbox) {
          try {
            this.lightbox.destroy();
          } catch(e) {}
          this.lightbox = null;
        }
        
        // Only create lightbox if there are items
        if (dataSource.length === 0) return;
        
        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });
        
        this.lightbox.addFilter('thumbEl', (thumbEl, { data }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }
        
          return thumbEl;
        });
        
        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }
        
          return placeholderSrc;
        });
        
        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { currIndex } = this.lightbox.pswp;
            
            if (currIndex >= 0 && currIndex < dataSource.length) {
              const currentItem = dataSource[currIndex];
              if (currentItem.originalIndex !== undefined) {
                const originalMedia = allMedia[currentItem.originalIndex];
                if (originalMedia) {
                  const mediaId = originalMedia.getAttribute('data-media-id');
                  
                  // Find this slide in the current filtered slider
                  const slides = this.sliderInstance.slider.slides;
                  let slideIndex = 0;
                  
                  for (let i = 0; i < slides.length; i++) {
                    if (slides[i].getAttribute('data-media-id') === mediaId) {
                      slideIndex = i;
                      break;
                    }
                  }
                                    
                  if (this.sliderInstance.slider.activeIndex !== slideIndex) {
                    this.sliderInstance.slider.slideTo(slideIndex, 100, false);
                  }
                }
              }
            }
          }
        });
        
        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });
        
        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="https://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });
        
        this.lightbox.init();
        
        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              // Get the current ACTIVE slide from swiper
              let index = 0;
              
              if (this.sliderInstance && this.sliderInstance.slider) {
                const activeSlide = this.sliderInstance.slider.slides[this.sliderInstance.slider.activeIndex];
                if (activeSlide) {
                  const activeMediaId = activeSlide.getAttribute('data-media-id');
                  
                  // Find this active media in the filtered dataSource
                  for (let i = 0; i < dataSource.length; i++) {
                    if (allMedia[dataSource[i].originalIndex]?.getAttribute('data-media-id') === activeMediaId) {
                      index = i;
                      break;
                    }
                  }
                }
              }
              
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }
      updateImageZoomForVariant(variant) {        
        // Destroy existing lightbox if it exists
        if (this.lightbox) {
          this.lightbox.destroy();
          this.lightbox = null;
        }
        
        // Reinitialize with filtered items
        this.initImageZoom();
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(swiper.realIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant || !variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        if (this.sliderInstance && this.sliderInstance.slider && this.sliderInstance.slider.slides) {
          const swiper = this.sliderInstance.slider;
          const slides = swiper.slides;
          
          if (swiper.params.loop) {
            let realIndex = 0;
            let found = false;
            for (let i = 0; i < slides.length; i++) {
              if (slides[i].classList.contains('swiper-slide-duplicate')) continue;
              if (slides[i] && slides[i].getAttribute && slides[i].getAttribute('data-media-id') == variant.featured_media.id) {
                found = true;
                break;
              }
              realIndex++;
            }
            if (found && swiper.realIndex !== realIndex) {
              swiper.slideToLoop(realIndex);
            }
          } else {
            let slideIndex = -1;
            for (let i = 0; i < slides.length; i++) {
              if (slides[i] && slides[i].getAttribute && slides[i].getAttribute('data-media-id') == variant.featured_media.id) {
                slideIndex = i;
                break;
              }
            }
            if (slideIndex !== -1 && swiper.activeIndex !== slideIndex) {
              swiper.slideTo(slideIndex);
            }
          }
        } else {
          this.sortMediaItems(variant);
        }
      }

// Add this method to the MediaGallery class
reinitSwiper() {
  // Destroy existing swipers
  if (this.sliderInstance && this.sliderInstance.slider) {
    this.sliderInstance.slider.destroy(true, true);
    this.sliderInstance = false;
  }
  if (this.thumbsInstance && this.thumbsInstance.slider) {
    this.thumbsInstance.slider.destroy(true, true);
    this.thumbsInstance = false;
  }
  
  // Reinitialize
  this.init();
}

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
